/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { BuildOutlined } from '@ant-design/icons';
import { Grid } from 'antd';
import { useState } from 'react';

import { NodeConfig } from '@/client';
import { GuardedButton } from '@/components/GuardedButton';
import { PageHeader } from '@/components/PageHeader';
import { useMessage } from '@/hooks/message';
import { useArchiveNodeConfigMutation } from '@/hooks/mutation/useArchiveConfigMutation';
import { useUnarchiveNodeConfigMutation } from '@/hooks/mutation/useUnarchiveConfigMutation';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Col } from '@/primitives/Col';
import { Flex } from '@/primitives/Flex';
import { OsIcon } from '@/primitives/Icons';
import { Row } from '@/primitives/Row';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { VerticalDivider } from '@/primitives/VerticalDivider';

export type NodeConfigurationDetailsHeaderProps = {
  nodeConfig: NodeConfig;
  onUploadRevisionClick?: () => void;
};

const { useBreakpoint } = Grid;

export function NodeConfigurationDetailsHeader({
  nodeConfig,
  onUploadRevisionClick,
}: NodeConfigurationDetailsHeaderProps) {
  const [submittingArchive, setSubmittingArchive] = useState(false);
  const { t } = useMimicTranslation('nodeConfigurationDetail');
  const [message] = useMessage();
  const screens = useBreakpoint();
  const { mutate: archiveNodeConfig } = useArchiveNodeConfigMutation({
    onSuccess: () => {
      message.success(t('header.successOnArchive'));
      setSubmittingArchive(false);
    },
    onError: () => {
      message.error(t('header.errorOnArchive'));
      setSubmittingArchive(false);
    },
    onMutate: () => {
      setSubmittingArchive(true);
    },
  });
  const { mutate: unarchiveNodeConfig } = useUnarchiveNodeConfigMutation({
    onSuccess: () => {
      message.success(t('header.successOnUnarchive'));
      setSubmittingArchive(false);
    },
    onError: () => {
      message.error(t('header.errorOnUnarchive'));
      setSubmittingArchive(false);
    },
    onMutate: () => {
      setSubmittingArchive(true);
    },
  });

  const onArchiveNodeConfig = () => {
    const action = nodeConfig.dateArchived ? unarchiveNodeConfig : archiveNodeConfig;
    action({ configId: nodeConfig.id });
  };

  const archiveDisabledMessage = () => {
    if (nodeConfig.dateArchived) {
      return null;
    }
    if (nodeConfig.default) {
      return t('header.disabledDefaultConfig');
    }
    if (nodeConfig.useCount > 0) {
      return t('header.disabledNodesAssigned');
    }
    return null;
  };

  const archiveButtonText = nodeConfig.dateArchived ? t('header.unarchive') : t('header.archive');

  return (
    <div style={{ padding: '24px 0 0 0' }}>
      <PageHeader
        icon={<BuildOutlined style={{ height: '16px', width: '16px' }} />}
        title={
          <Text type="subtle" size="md" strong style={{ lineHeight: '28px' }}>
            {t('title')}
          </Text>
        }
        level={5}
        fontWeight="300"
      />
      <Row gutter={[16, 16]}>
        <Col span={24} xl={14}>
          <Flex vertical align="left" gap="xs">
            <Title level={4} style={{ fontFamily: 'DM Mono', fontWeight: 500 }}>
              {nodeConfig.name}
            </Title>
            <Flex gap="xs" wrap>
              <Text size="sm" type="subtle" style={{ textTransform: 'capitalize' }}>
                <OsIcon name={nodeConfig.os} /> {nodeConfig.os}
              </Text>
              <VerticalDivider />
              <Text mono copyable type="subtle" size="sm">
                {nodeConfig.id}
              </Text>
            </Flex>
          </Flex>
        </Col>
        {useAuthorization('superadmin') && (
          <Col xs={24} xl={10}>
            <Flex
              full
              gap={screens.xl ? 'xs' : 'none'}
              justify={screens.xl ? 'flex-end' : 'space-between'}
              align="center"
            >
              <Flex gap="xs">
                <Flex vertical align="left" gap="xs">
                  <GuardedButton
                    requiredRole="superadmin"
                    type="default"
                    dataTestId="archive-config"
                    loading={submittingArchive}
                    onClick={onArchiveNodeConfig}
                    disabled={!!archiveDisabledMessage()}
                  >
                    {archiveButtonText}
                  </GuardedButton>
                  {archiveDisabledMessage() && (
                    <Text data-testid="config-archive-disabled" size="xs">
                      {archiveDisabledMessage()}
                    </Text>
                  )}
                </Flex>
                {onUploadRevisionClick && !nodeConfig.dateArchived && (
                  <GuardedButton
                    requiredRole="superadmin"
                    type="primary"
                    dataTestId="upload-revision-button"
                    onClick={onUploadRevisionClick}
                  >
                    {t('header.uploadRevision')}
                  </GuardedButton>
                )}
              </Flex>
            </Flex>
          </Col>
        )}
      </Row>
    </div>
  );
}
